'use client';
// ──────────────────────────────────────────────────────
// AuthenticityCard — "أصلي 100% ✅" reassurance card
// Rendered inside ProductContent (popup + scan page)
// ──────────────────────────────────────────────────────
import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

interface AuthenticityCardProps {
    productId: string;
}

const LEGAL_TEXT = `هذا المنتج أصلي 100% ومُوثّق من Q Care ضمن منظومة Q Supermarket، وقد تم توريده عبر قنوات شراء معتمدة من موردين معتمدين لدينا.
في حال وجود أي ملاحظة تتعلق بأصالة المنتج، يُرجى مراجعة موظف القسم فورًا أو خدمة العملاء وفق سياسة الاستبدال والاسترجاع المعتمدة لدى Q Supermarket.
تسري الشروط والأحكام المعتمدة لدى Q Supermarket.`;

export function AuthenticityCard({ productId }: AuthenticityCardProps) {
    const [showLegal, setShowLegal] = useState(false);
    const qrRef = useRef<HTMLCanvasElement>(null);
    const proofUrl = typeof window !== 'undefined'
        ? `${window.location.origin}/products/${productId}/proof`
        : `/products/${productId}/proof`;

    // Generate QR code client-side
    useEffect(() => {
        if (!qrRef.current) return;
        import('qrcode').then(QRCode => {
            QRCode.toCanvas(qrRef.current, proofUrl, {
                width: 80,
                margin: 1,
                color: { dark: '#0d8f8f', light: '#ffffff' },
            });
        }).catch(() => {
            // QR library not available, graceful degradation
        });
    }, [proofUrl]);

    return (
        <>
            <div className="rounded-2xl px-5 py-4" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                        {/* Headline */}
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">🛡️</span>
                            <h3 className="text-sm font-bold" style={{ color: '#166534' }}>
                                أصلي 100% ✅
                            </h3>
                        </div>

                        {/* Reassurance lines */}
                        <div className="space-y-1 mb-3">
                            <p className="text-xs font-medium" style={{ color: '#15803d' }}>
                                ✓ موثّق من Q Care
                            </p>
                            <p className="text-xs font-medium" style={{ color: '#15803d' }}>
                                ✓ مورّد عبر قنوات شراء معتمدة
                            </p>
                        </div>

                        {/* Action buttons */}
                        <div className="flex flex-wrap gap-2">
                            <Link
                                href={`/products/${productId}/proof`}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:shadow-sm active:scale-95"
                                style={{ background: '#166534', color: '#ffffff' }}
                            >
                                اعرض الإثبات
                            </Link>
                            <button
                                onClick={() => setShowLegal(true)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-green-100"
                                style={{ color: '#166534', border: '1px solid #bbf7d0' }}
                            >
                                الشروط
                            </button>
                        </div>
                    </div>

                    {/* QR Code */}
                    <div className="flex-shrink-0">
                        <canvas ref={qrRef} className="rounded-lg" style={{ width: 80, height: 80 }} />
                        <p className="text-[9px] text-center mt-1" style={{ color: '#6b7280' }}>
                            امسح للتحقق
                        </p>
                    </div>
                </div>
            </div>

            {/* Legal text modal */}
            {showLegal && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center px-4"
                    style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}
                    onClick={() => setShowLegal(false)}
                >
                    <div
                        className="max-w-md w-full rounded-2xl p-6 space-y-4"
                        style={{ background: '#ffffff', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}
                        onClick={e => e.stopPropagation()}
                        dir="rtl"
                    >
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-bold" style={{ color: '#1a1a2e' }}>
                                🛡️ الشروط والأحكام
                            </h3>
                            <button
                                onClick={() => setShowLegal(false)}
                                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition"
                                style={{ color: '#94a3b8' }}
                            >
                                ✕
                            </button>
                        </div>
                        <p className="text-xs leading-relaxed whitespace-pre-line" style={{ color: '#475569' }}>
                            {LEGAL_TEXT}
                        </p>
                    </div>
                </div>
            )}
        </>
    );
}
